import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { campaignAPI } from '../../services/campaignAPI';

// Helper function to transform campaign data
const transformCampaignData = (campaign) => {
  const calculateProgress = (campaign) => {
    // If backend already calculated progress, use it
    if (campaign.progress !== undefined && campaign.progress !== null) {
      return campaign.progress;
    }
    
    // Otherwise, calculate from count_data
    const countData = campaign.count_data || {};
    const campaignType = campaign.campaign_type;
    const goalType = campaign.goal_type;
    
    // Pre-sale (campaign_type = '0')
    if (campaignType === '0') {
      const itemsSold = countData.items_sold || 0;
      const isLimitInventory = campaign.is_limit_inventory === '1' || campaign.is_limit_inventory === 1;
      const availableInventory = parseInt(campaign.available_inventory) || 0;
      
      if (isLimitInventory && availableInventory > 0) {
        return Math.min(Math.round((itemsSold / availableInventory) * 100), 100);
      }
      return 0;
    }
    // Product Drop (campaign_type = '2')
    else if (campaignType === '2') {
      const itemsSold = countData.items_sold || 0;
      const totalDropLimit = parseInt(campaign.product_drop_total_limit) || 0;
      
      if (totalDropLimit > 0) {
        return Math.min(Math.round((itemsSold / totalDropLimit) * 100), 100);
      }
      return 0;
    }
    // Crowdfunding (campaign_type = '1')
    else if (campaignType === '1') {
      const isAddStartPoint = campaign.is_add_start_point === '1' || campaign.is_add_start_point === 1;
      
      if (goalType === '0') {
        // Total Items Sold goal
        const itemsSold = countData.items_sold || 0;
        const initialBackers = isAddStartPoint ? (parseInt(campaign.initial_backers) || 0) : 0;
        const totalItems = parseInt(campaign.number_of_items) || 0;
        if (totalItems > 0) {
          return Math.min(Math.round(((itemsSold + initialBackers) / totalItems) * 100), 100);
        }
        return 0;
      } else {
        // Total Amount goal
        const amountRaised = parseFloat(countData.amount_raised) || 0;
        const initialFund = isAddStartPoint ? (parseFloat(campaign.initial_fund) || 0) : 0;
        const totalAmount = parseFloat(campaign.total_amount) || 0;
        if (totalAmount > 0) {
          return Math.min(Math.round(((amountRaised + initialFund) / totalAmount) * 100), 100);
        }
        return 0;
      }
    }
    
    return 0;
  };

  // Parse product_id if it's a JSON string, otherwise use as-is
  let productIdObj = campaign.product_id;
  if (typeof productIdObj === 'string') {
    try {
      productIdObj = JSON.parse(productIdObj);
    } catch (e) {
      // If parsing fails, keep as string or set to null
      productIdObj = null;
    }
  }

  return {
    ...campaign,
    id: parseInt(campaign.id),
    image: campaign.product_image || campaign.campaign_banner_link || 'https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=100&h=100&fit=crop&crop=center',
    campaign_end_datetime: campaign.campaign_end_datetime ? new Date(campaign.campaign_end_datetime) : null,
    campaign_start_datetime: campaign.campaign_start_datetime ? new Date(campaign.campaign_start_datetime) : null,
    initial_fund: parseFloat(campaign.initial_fund) || 0,
    total_amount: parseFloat(campaign.total_amount) || 0,
    number_of_items: parseInt(campaign.number_of_items) || 0,
    initial_backers: parseInt(campaign.initial_backers) || 0,
    progress: calculateProgress(campaign),
    product_id: productIdObj, // Preserve product_id object with handle
    count_data: campaign.count_data || {
      items_sold: 0,
      amount_raised: 0,
      reward_count: 0,
      reward_items_sold: 0,
      reward_amount_raised: 0
    }, // Preserve count_data from API
  };
};

// Async thunk to fetch all campaigns with pagination support
export const fetchCampaigns = createAsyncThunk(
  'campaign/fetchCampaigns',
  async ({ clientId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await campaignAPI.getCampaigns(clientId, page, limit);
      
      if (response.success && Array.isArray(response.data)) {
        const transformedCampaigns = response.data.map(transformCampaignData);
        // Return both campaigns and pagination info
        return {
          campaigns: transformedCampaigns,
          pagination: response.pagination || null
        };
      }
      
      throw new Error(response.message || 'Failed to fetch campaigns');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch campaigns');
    }
  }
);

// Async thunk to fetch filtered campaigns
export const fetchFilteredCampaigns = createAsyncThunk(
  'campaign/fetchFilteredCampaigns',
  async (filters, { rejectWithValue }) => {
    try {
      const response = await campaignAPI.getFilteredCampaigns(filters);
      
      if (response.success && Array.isArray(response.data)) {
        const transformedCampaigns = response.data.map(transformCampaignData);
        // Return both campaigns and pagination info
        return {
          campaigns: transformedCampaigns,
          pagination: response.pagination || null
        };
      }
      
      throw new Error(response.message || 'Failed to fetch filtered campaigns');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch filtered campaigns');
    }
  }
);

// Async thunk to update campaign status
export const updateCampaignStatus = createAsyncThunk(
  'campaign/updateStatus',
  async ({ id, status, clientId }, { rejectWithValue, getState }) => {
    try {
      const response = await campaignAPI.updateCampaignStatus(id, status, clientId);
      
      if (response.success) {
        // Update the campaign in Redux state
        const state = getState();
        const campaigns = state.campaign.campaigns;
        const updatedCampaigns = campaigns.map(campaign => 
          campaign.id === parseInt(id) 
            ? { ...campaign, campaign_status: status }
            : campaign
        );
        
        return { campaigns: updatedCampaigns, message: response.message };
      }
      
      throw new Error(response.message || 'Failed to update campaign status');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update campaign status');
    }
  }
);

const campaignSlice = createSlice({
  name: 'campaign',
  initialState: {
    campaigns: [],
    filteredCampaigns: [],
    loading: false,
    filteredLoading: false,
    error: null,
    hasCampaignsEver: false,
    lastFetchTime: null,
    pagination: null, // Store pagination info: { page, limit, total, totalPages, hasNextPage, hasPreviousPage }
  },
  reducers: {
    setCampaigns: (state, action) => {
      state.campaigns = action.payload;
      if (action.payload.length > 0) {
        state.hasCampaignsEver = true;
      }
    },
    setFilteredCampaigns: (state, action) => {
      state.filteredCampaigns = action.payload;
    },
    updateCampaignInList: (state, action) => {
      const { id, updates } = action.payload;
      state.campaigns = state.campaigns.map(campaign =>
        campaign.id === parseInt(id) ? { ...campaign, ...updates } : campaign
      );
      state.filteredCampaigns = state.filteredCampaigns.map(campaign =>
        campaign.id === parseInt(id) ? { ...campaign, ...updates } : campaign
      );
    },
    removeCampaign: (state, action) => {
      const id = parseInt(action.payload);
      state.campaigns = state.campaigns.filter(campaign => campaign.id !== id);
      state.filteredCampaigns = state.filteredCampaigns.filter(campaign => campaign.id !== id);
    },
    clearFilteredCampaigns: (state) => {
      state.filteredCampaigns = [];
    },
    resetCampaigns: (state) => {
      state.campaigns = [];
      state.filteredCampaigns = [];
      state.loading = false;
      state.filteredLoading = false;
      state.error = null;
      state.hasCampaignsEver = false;
      state.lastFetchTime = null;
      state.pagination = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch campaigns
      .addCase(fetchCampaigns.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCampaigns.fulfilled, (state, action) => {
        state.loading = false;
        // Handle both old format (array) and new format (object with campaigns and pagination)
        if (Array.isArray(action.payload)) {
          state.campaigns = action.payload;
          state.pagination = null;
        } else {
          state.campaigns = action.payload.campaigns || [];
          state.pagination = action.payload.pagination || null;
        }
        state.lastFetchTime = Date.now();
        if (state.campaigns.length > 0) {
          state.hasCampaignsEver = true;
        }
      })
      .addCase(fetchCampaigns.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch campaigns';
        state.pagination = null;
      })
      // Fetch filtered campaigns
      .addCase(fetchFilteredCampaigns.pending, (state) => {
        state.filteredLoading = true;
        state.error = null;
      })
      .addCase(fetchFilteredCampaigns.fulfilled, (state, action) => {
        state.filteredLoading = false;
        // Handle both old format (array) and new format (object with campaigns and pagination)
        if (Array.isArray(action.payload)) {
          state.filteredCampaigns = action.payload;
          state.pagination = null;
        } else {
          state.filteredCampaigns = action.payload.campaigns || [];
          state.pagination = action.payload.pagination || null;
        }
      })
      .addCase(fetchFilteredCampaigns.rejected, (state, action) => {
        state.filteredLoading = false;
        state.error = action.payload || 'Failed to fetch filtered campaigns';
        state.pagination = null;
      })
      // Update campaign status
      .addCase(updateCampaignStatus.fulfilled, (state, action) => {
        state.campaigns = action.payload.campaigns;
      });
  },
});

export const {
  setCampaigns,
  setFilteredCampaigns,
  updateCampaignInList,
  removeCampaign,
  clearFilteredCampaigns,
  resetCampaigns,
} = campaignSlice.actions;

export default campaignSlice.reducer;
